import {
  Button,
  EmptyState,
  SkeletonRows,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  cn,
  useToast,
} from '@ambernord/ui';
import { ShoppingCart } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';

interface OrderRow {
  id: string;
  orderNo: string;
  market: string;
  milestone?: string;
  status?: string;
  createdAt?: string | null;
}

type Filter = 'all' | 'active' | 'done';

function isPlatformRole(role: string): boolean {
  return role.startsWith('platform_') || role === 'auditor';
}

/** Pabeigts = termināls statuss (platformai) vai piegādāts milestone (klientam). */
function isDone(o: OrderRow, platform: boolean): boolean {
  if (platform) return o.status === 'paid' || o.status === 'cancelled';
  return o.milestone === 'delivered';
}

export default function Orders() {
  const { t, i18n } = useTranslation(['orders', 'statuses']);
  const lang = i18n.language;
  const { user } = useAuth();
  const { toast } = useToast();
  const platform = user ? isPlatformRole(user.role) : false;

  const [items, setItems] = useState<OrderRow[] | null>(null);
  const [error, setError] = useState(false);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    const res = await api.get<{ items: OrderRow[] }>('/api/orders');
    if (!res.ok) {
      setError(true);
      return;
    }
    setError(false);
    setItems(res.data.items ?? []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function accept(id: string) {
    setAccepting(id);
    try {
      // Piegādātājs nav obligāts — backend izvēlas pirmo (demo).
      const res = await api.post<{ error?: string }>(`/api/orders/${id}/accept`, {});
      if (!res.ok) {
        toast(res.data.error ?? t('error'), 'error');
        return;
      }
      toast(t('toast.accepted'));
      await load();
    } finally {
      setAccepting(null);
    }
  }

  const counts = useMemo(() => {
    const all = items?.length ?? 0;
    const done = (items ?? []).filter((o) => isDone(o, platform)).length;
    return { all, active: all - done, done };
  }, [items, platform]);

  const filtered = useMemo(
    () =>
      (items ?? []).filter((o) => {
        if (filter === 'all') return true;
        return filter === 'done' ? isDone(o, platform) : !isDone(o, platform);
      }),
    [items, filter, platform],
  );

  function statusCell(o: OrderRow) {
    if (platform) {
      return o.status ? <StatusBadge status={o.status} label={t(`statuses:${o.status}`)} /> : '—';
    }
    return o.milestone ? (
      <StatusBadge
        status={o.milestone}
        label={t(`milestone.${o.milestone}`, { defaultValue: o.milestone })}
      />
    ) : (
      '—'
    );
  }

  const loaded = items !== null;
  const newOrderCta = !platform && (
    <Link to="/orders/new">
      <Button size="sm">
        <ShoppingCart className="mr-1.5 h-4 w-4" aria-hidden />
        {t('actions.newOrder')}
      </Button>
    </Link>
  );

  return (
    <AppLayout title={t('title')}>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {platform ? t('intro.platform') : t('intro.customer')}
        </p>
        {newOrderCta}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {(['all', 'active', 'done'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
              filter === f
                ? 'border-brand-600 bg-brand-600 text-white'
                : 'border-slate-300 bg-white text-slate-600 hover:border-brand-400 hover:text-brand-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:text-brand-300',
            )}
          >
            {t(`filters.${f}`)}
            <span
              className={cn(
                'rounded-full px-1.5 text-[10px] font-bold',
                filter === f
                  ? 'bg-white/20'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
              )}
            >
              {counts[f]}
            </span>
          </button>
        ))}
      </div>

      {!loaded && !error && <SkeletonRows rows={5} />}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {t('error')}
        </p>
      )}

      {loaded && !error && items.length === 0 && (
        <EmptyState
          icon={<ShoppingCart className="h-6 w-6" aria-hidden />}
          title={t('empty.title')}
          hint={platform ? t('empty.hintPlatform') : t('empty.hint')}
          action={newOrderCta}
        />
      )}

      {loaded && !error && items.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon={<ShoppingCart className="h-6 w-6" aria-hidden />}
          title={t('empty.noMatches')}
        />
      )}

      {loaded && !error && filtered.length > 0 && (
        <TableShell>
          <THead>
            <tr>
              <Th>{t('columns.order')}</Th>
              <Th>{t('columns.market')}</Th>
              <Th>{t('columns.created')}</Th>
              <Th>{platform ? t('columns.status') : t('columns.milestone')}</Th>
              {platform && <Th>{t('columns.action')}</Th>}
            </tr>
          </THead>
          <tbody>
            {filtered.map((o) => (
              <Tr key={o.id}>
                <Td className="font-medium text-slate-900 dark:text-white">{o.orderNo}</Td>
                <Td className="text-slate-500 dark:text-slate-400">{o.market.toUpperCase()}</Td>
                <Td className="text-slate-500 dark:text-slate-400">
                  {formatDate(o.createdAt, lang)}
                </Td>
                <Td>{statusCell(o)}</Td>
                {platform && (
                  <Td>
                    {o.status === 'placed' ? (
                      <Button
                        size="sm"
                        disabled={accepting === o.id}
                        onClick={() => void accept(o.id)}
                      >
                        {accepting === o.id ? t('loading') : t('actions.accept')}
                      </Button>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </Td>
                )}
              </Tr>
            ))}
          </tbody>
        </TableShell>
      )}
    </AppLayout>
  );
}
